import React, { useCallback } from 'react';
import { Button } from '@moda/om';
import ExpandIcon from '@moda/icons/expand-16';
import { DesignerChip } from '../../components/DesignerChip';
import { useUser } from '../../hooks/useUser';
import {
  useDesignerPreferences,
  useFollowDesigner,
  useUnfollowDesigner
} from '../../hooks/useDesignerPreferences';
import { AccountDesignerPreferencesVertical } from '../../hooks/useDesigners';
import { TrunkshowPageQuery } from '../../generated/types';
import { useAuthModal } from '../../hooks/useAuthModal';

import './TrunkshowHeaderBrandFollowButton.scss';

type TrunkshowHeaderModuleTrunkshow = NonNullable<
  NonNullable<TrunkshowPageQuery['trunkshowPage']>['trunkshow']
>;

export interface Props {
  trunkshow: TrunkshowHeaderModuleTrunkshow;
}

export const TrunkshowHeaderBrandFollowButton: React.FC<Props> = ({
  trunkshow: { designersData }
}) => {
  const {
    user: { isLoggedIn }
  } = useUser();
  const { openAuthModal } = useAuthModal();
  const { data: designerPreferencesData } = useDesignerPreferences(
    AccountDesignerPreferencesVertical.Women
  );
  const designerData = designersData?.[0];
  const isDesignerFollowed =
    designerPreferencesData?.find(({ designerId }) => designerId === Number(designerData?.id)) !==
    undefined;

  const { followDesigner } = useFollowDesigner(AccountDesignerPreferencesVertical.Women);
  const { unfollowDesigner } = useUnfollowDesigner(AccountDesignerPreferencesVertical.Women);

  const handleFollow = useCallback(
    (id: string, name: string | null) => followDesigner(id, name),
    [followDesigner]
  );

  const handleFollowCheck = useCallback(
    (id: string, name: string | null) => {
      openAuthModal({ onSuccess: () => followDesigner(id, name) });
    },
    [followDesigner, openAuthModal]
  );

  const handleUnfollow = useCallback(
    (id: string, name: string | null) => unfollowDesigner(id, name),
    [unfollowDesigner]
  );

  if (!designerData) return null;

  return (
    <div className="TrunkshowHeaderBrandFollowButton">
      {!isLoggedIn && (
        <Button
          chip
          secondary
          className="TrunkshowHeaderBrandFollowButton__wrapper"
          onClick={() => handleFollowCheck(designerData.id, designerData.name)}
          data-testid="TrunkshowHeaderBrandFollowButton__wrapper"
        >
          <span className="TrunkshowHeaderBrandFollowButton__text">Follow for Updates</span>
          <ExpandIcon />
        </Button>
      )}

      {isLoggedIn && (
        <DesignerChip
          id={designerData.id}
          key={designerData.id}
          name={designerData.name}
          isFollowed={isDesignerFollowed}
          onFollow={handleFollow}
          onUnfollow={handleUnfollow}
          withHoveringEffect
          treatment="generic"
        />
      )}
    </div>
  );
};
